<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useMeta } from "/src/composables/use-meta";
import axios from 'axios';
import { BASE_URL, BASE_URL_3 } from '/src/Config/config';
import { useStore } from 'vuex';
import Swal from 'sweetalert2';
import { useRouter } from 'vue-router';

useMeta({ title: "Detailed Reports" });

const store = useStore();

const router = useRouter();

// Permission checks
const hasEditPermission = computed(() => store.getters.hasPermission('edit_violation_actions'));
const hasDeletePermission = computed(() => store.getters.hasPermission('delete_violation_actions'));
const hasPrintABSDetailedReportsPermission = computed(() => store.getters.hasPermission('print_abs_detailed_report'));

// Get username from store
const username = computed(() => {
    const user = store.state.user;
    return user?.username || user?.name || 'User';
});

// Modal state
const showEditModal = ref(false);
const currentEditItem = ref(null);
const isSubmitting = ref(false);

// Search and filter variables
const searchValue = ref('');
const startDate = ref('');
const endDate = ref('');
const isLoading = ref(false);
const selectedViolationId = ref('');
const selectedShift = ref('');

// Data variables
const violations = ref([]);
const violationActions = ref([]);
const drivers = ref([]);
const actionsTaken = ref([]);
const violationParameters = ref([]);

// Store parameter names for each violation
const violationParamMap = ref({});

// Pagination variables
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = computed(() => Math.ceil(filteredActions.value.length / itemsPerPage.value));

const editForm = ref({
    driver_id: '',
    conductor_id: '',
    bus_registration: '',
    route: '',
    schedule: '',
    taken_action_id: '',
    // Add other fields as needed
});

// Open edit modal
const openEditModal = (action) => {
    currentEditItem.value = action;
    editForm.value = {
        driver_id: action.driver_id,
        conductor_id: action.conductor_id,
        bus_registration: action.bus_registration,
        route: action.route,
        schedule: action.schedule,
        taken_action_id: action.taken_action_id,
        // Copy other relevant fields
    };
    showEditModal.value = true;
};

// Submit edit
const submitEdit = async () => {
    try {
        isSubmitting.value = true;
        await axios.post(`${BASE_URL}/abs-violation-actions/update/${currentEditItem.value.id}`, editForm.value, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Violation action updated successfully',
        });

        // Refresh data
        await loadViolationActions();
        showEditModal.value = false;
    } catch (error) {
        console.error('Error updating violation action:', error);
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to update violation action',
        });
    } finally {
        isSubmitting.value = false;
    }
};

// Delete action
const confirmDelete = (action) => {
    Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
        if (result.isConfirmed) {
            try {
                await axios.delete(`${BASE_URL}/abs-violation-actions/destroy/${action.id}`, {
                    headers: {
                        'Authorization': `Bearer ${store.state.token}`,
                        'X-Company-ID': store.state.currentCompany?.id
                    }
                });

                Swal.fire(
                    'Deleted!',
                    'Violation action has been deleted.',
                    'success'
                );

                // Refresh data
                await loadViolationActions();
            } catch (error) {
                console.error('Error deleting violation action:', error);
                Swal.fire(
                    'Error!',
                    'Failed to delete violation action',
                    'error'
                );
            }
        }
    });
};

// Get all unique parameter names for the selected violation
const currentViolationParams = computed(() => {
    if (!selectedViolationId.value) return [];
    return violationParamMap.value[selectedViolationId.value] || [];
});

const filteredParams = computed(() => {
    if (!selectedViolationId.value) return [];
    return violationParameters.value.filter(param =>
        param.violation_id === selectedViolationId.value && param.is_active
    );
});

const actionsWithShifts = computed(() => {
    return violationActions.value.map(action => {
        if (!action.created_at) return { ...action, shift: 'unknown' };

        const actionTime = new Date(action.created_at);
        const hours = actionTime.getHours();
        const minutes = actionTime.getMinutes();

        // Convert to minutes since midnight for easier comparison
        const totalMinutes = hours * 60 + minutes;

        // Day shift: 08:30 (510 mins) to 20:30 (1230 mins)
        // Night shift: 20:30 (1230 mins) to 08:30 (510 mins next day)
        let shift;
        if (totalMinutes >= 510 && totalMinutes < 1230) {
            shift = 'day';
        } else {
            shift = 'night';
        }

        return { ...action, shift };
    });
});

const shiftCounts = computed(() => {
    const counts = {
        day: 0,
        night: 0,
        all: actionsWithShifts.value.length
    };

    actionsWithShifts.value.forEach(action => {
        if (action.shift === 'day') counts.day++;
        if (action.shift === 'night') counts.night++;
    });

    return counts;
});

const filteredActions = computed(() => {
    // let filtered = violationActions.value;
    let filtered = actionsWithShifts.value;

    // Filter by shift
    if (selectedShift.value) {
        filtered = filtered.filter(action => action.shift === selectedShift.value);
    }

    // Filter by violation
    if (selectedViolationId.value) {
        filtered = filtered.filter(action => action.violation_id == selectedViolationId.value);
    }

    // Filter by date range
    if (startDate.value && endDate.value) {
        filtered = filtered.filter(action => {
            const actionDate = action.created_at ? new Date(action.created_at) : null;
            if (!actionDate) return false;
            return (
                actionDate >= new Date(startDate.value) &&
                actionDate <= new Date(endDate.value + 'T23:59:59')
            );
        });
    }

    // Filter by search term
    if (searchValue.value) {
        const keyword = searchValue.value.toLowerCase();
        filtered = filtered.filter(action => {
            const driver = drivers.value.find(d => d.id == action.driver_id);
            const actionTaken = actionsTaken.value.find(a => a.id == action.taken_action_id);

            return (
                (action.driver_id || '').toLowerCase().includes(keyword) ||
                (action.registration || '').toLowerCase().includes(keyword) ||
                (action.route || '').toLowerCase().includes(keyword) ||
                (action.violation?.title || '').toLowerCase().includes(keyword) ||
                (actionTaken?.name || '').toLowerCase().includes(keyword)
            );
        });
    }

    return filtered;
});

// Paginated data
const paginatedActions = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredActions.value.slice(start, end);
});

const violationCounts = computed(() => {
    const counts = {};

    // Initialize counts for all violations
    violations.value.forEach(violation => {
        counts[violation.id] = {
            title: violation.title,
            count: 0
        };
    });

    // Add 'All' option
    counts.all = {
        title: 'All Violations',
        count: violationActions.value.length
    };

    // Calculate counts for each violation
    violationActions.value.forEach(action => {
        if (counts[action.violation_id]) {
            counts[action.violation_id].count++;
        }
    });

    return counts;
});

const currentViolationCount = computed(() => {
    if (!selectedViolationId.value) {
        return violationCounts.value.all.count;
    }
    return violationCounts.value[selectedViolationId.value]?.count || 0;
});

const filteredViolationCounts = computed(() => {
    const counts = {};

    // Initialize counts for all violations
    violations.value.forEach(violation => {
        counts[violation.id] = {
            title: violation.title,
            count: 0
        };
    });

    // Add 'All' option
    counts.all = {
        title: 'All Violations',
        count: 0
    };

    // Calculate counts based on filtered actions
    filteredActions.value.forEach(action => {
        counts.all.count++;
        if (counts[action.violation_id]) {
            counts[action.violation_id].count++;
        }
    });

    return counts;
});

// Pagination methods
const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const firstPage = () => {
    currentPage.value = 1;
};

const lastPage = () => {
    currentPage.value = totalPages.value;
};

// Reset to first page when filters change
watch([searchValue, startDate, endDate, selectedViolationId], () => {
    currentPage.value = 1;
});

const formatDate = (dateString) => {
    if (!dateString) return '—';
    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    return new Date(dateString).toLocaleString(undefined, options);
};

const loadViolations = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/violations`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violations.value = response.data.data || response.data;
        console.log("Violations loaded:", violations.value);
    } catch (error) {
        console.error('Error fetching violations:', error);
    }
};

// Load Violation Actions
const loadViolationActions = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/abs-violation-actions/index`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        const actions = response.data.data || response.data;

        violationActions.value = actions.map(action => {
            // Get numeric driver ID
            const driverId = typeof action.driver_id === 'string'
                ? parseInt(action.driver_id)
                : action.driver_id;

            // Find matching driver
            const driver = drivers.value.find(d => d.id === driverId);

            return {
                ...action,
                // Store both the numeric ID and the driver's actual name
                driver_id: driverId,
                driver_id: driver?.name || action.driver_id
            };
        });

        console.log("Mapped violation actions:", violationActions.value);
    } catch (error) {
        console.error('Error loading violation actions:', error);
    }
};

const loadDrivers = async () => {
    try {
        const response = await axios.get(`${BASE_URL_3}/company/employees/list?company_id=2`);
        drivers.value = response.data.employees || response.data;
        console.log("Drivers loaded:", drivers.value);
    } catch (error) {
        console.error('Error fetching employees:', error);
    }
};

const loadActionsTaken = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/taken-actions`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        actionsTaken.value = response.data.data || response.data;
        console.log("Actions Taken loaded:", actionsTaken.value);
    } catch (error) {
        console.error('Error fetching actions taken:', error);
    }
};

const loadViolationParameters = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/violation-parameters`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violationParameters.value = response.data.data || response.data;

        // Build parameter map for each violation
        violationParameters.value.forEach(param => {
            if (!violationParamMap.value[param.violation_id]) {
                violationParamMap.value[param.violation_id] = [];
            }
            violationParamMap.value[param.violation_id].push(param.name);
        });

        console.log("Violation Parameters loaded:", violationParameters.value);
    } catch (error) {
        console.error('Error fetching violation parameters:', error);
    }
};

// Map IDs to names
const getDriverName = (driverId) => {
    // Convert driverId to number if it's stored as string in violation actions
    const idToFind = typeof driverId === 'string' ? parseInt(driverId) : driverId;

    // Find the driver in the drivers list
    const driver = drivers.value.find(d => d.id === idToFind);

    // Return the driver's name if found, otherwise return the original ID
    return driver?.name || driverId || '—';
};

const getActionTakenName = (actionTakenId) => {
    const action = actionsTaken.value.find(a => a.id == actionTakenId);
    return action?.name || actionTakenId || '—';
};

// Data loading
onMounted(async () => {
    isLoading.value = true;
    try {
        await loadDrivers();

        // Load all required data in parallel
        await Promise.all([
            loadViolations(),
            loadViolationActions(),
            loadActionsTaken(),
            loadViolationParameters()
        ]);

    } catch (err) {
        console.error("Failed to load data:", err);
    } finally {
        isLoading.value = false;
    }
});

const printReport = () => {
    const originalItemsPerPage = itemsPerPage.value;
    itemsPerPage.value = 10000; // Large number to show all items in print

    setTimeout(() => {
        const tableContent = document.querySelector('table.table-bordered')?.cloneNode(true);
        if (!tableContent) return;

        // Remove pagination controls from print
        ['.pagination', '.items-per-page-selector'].forEach(selector => {
            tableContent.querySelectorAll(selector).forEach(el => el.remove());
        });

        // Get the selected violation title
        let reportTitle = 'Violations Report';
        if (selectedViolationId.value) {
            const selectedViolation = violations.value.find(v => v.id == selectedViolationId.value);
            if (selectedViolation) {
                reportTitle = `Violation Report: ${selectedViolation.title}`;
            }
        }

        // Date range text
        let dateRangeText = ''
        if (startDate.value && endDate.value) {
            dateRangeText = `From ${startDate.value} to ${endDate.value}`
        }

        const printWindow = window.open('', '', 'width=800,height=900');
        if (!printWindow) return;

        printWindow.document.write(`
            <html>
                <head>
                    <title>${reportTitle}</title>
                    <style>
                        @page {
                            size: A4 portrait;
                            margin: 10pt;
                        }
                        body {
                            font-family: Arial, sans-serif;
                            font-size: 12px;
                            padding: 15px;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .header {
                            text-align: center;
                            margin-bottom: 20px;
                        }
                        .header .title {
                            font-size: 24px;
                            font-weight: bold;
                            color: red;
                        }
                        .sub-header {
                            display: flex;
                            justify-content: space-between;
                            margin-bottom: 10px;
                        }
                        .report-title {
                            font-size: 18px;
                            font-weight: bold;
                            text-decoration: underline;
                        }
                        .date-range { font-weight: bold; margin-bottom: 10px; font-size: 14px; }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 20px;
                        }
                        th, td {
                            border: 1px solid #000 !important;
                            padding: 8px;
                            text-align: left;
                        }
                        td {
                            font-size: 12px;
                        }
                        th {
                            background-color: #f8f9fa !important;
                            font-weight: bold;
                        }
                        .footer {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            margin-top: 40px;
                            border-top: 2px solid #000;
                            padding-top: 10px;
                            font-size: 12px;
                            flex-wrap: wrap;
                            gap: 10px;
                        }
                        .footer > div {
                            min-width: 18%;
                        }
                        .print-actions {
                            display: flex;
                            justify-content: center;
                            margin: 20px 0;
                            gap: 10px;
                        }
                        button {
                            padding: 8px 16px;
                            border: none;
                            border-radius: 4px;
                            cursor: pointer;
                            font-weight: bold;
                        }
                        .btn-print {
                            background-color: #4CAF50;
                            color: white;
                        }
                        .btn-pdf {
                            background-color: #2196F3;
                            color: white;
                        }
                        .btn-close {
                            background-color: #f44336;
                            color: white;
                        }
                        @media print {
                            .print-actions {
                                display: none !important;
                            }
                        }
                        tr {
                            page-break-inside: avoid;
                        }
                    </style>
                    <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"><\/script>
                </head>
                <body>
                    <div id="content-to-export">
                        <div class="header">
                            <div class="title">ABOOD BUS SERVICE LIMITED</div>
                            <div>P.O Box 127 Msamvu Morogoro, Tanzania</div>
                            <div>Mob: +255 (0) 23 261 3589</div>
                        </div>
                        <div class="sub-header">
                            <div class="report-title">${reportTitle}</div>
                            ${dateRangeText ? `<div class="date-range">${dateRangeText}</div>` : ''}
                        </div>
                        <div class="content">
                            ${tableContent.outerHTML}
                        </div>
                        <div class="footer">
                            <div>Prepared By: ${username.value}<br>IT Officer</div>
                            <div>____________________<br>HR</div>
                            <div>_____________________<br>Accounts</div>
                            <div>_____________________<br>GM/MD</div>
                        </div>
                    </div>
                    <div class="print-actions">
                        <button onclick="window.print()" class="btn-print">Print Report</button>
                        <button onclick="downloadPDF()" class="btn-pdf">Download as PDF</button>
                        <button onclick="window.close()" class="btn-close">Close</button>
                    </div>
                    <script>
                        function downloadPDF() {
                            const element = document.getElementById('content-to-export');
                            const opt = {
                                margin: 10,
                                filename: 'detailed_violation_report.pdf',
                                image: { type: 'jpeg', quality: 0.98 },
                                html2canvas: { 
                                    scale: 2,
                                    logging: true,
                                    useCORS: true
                                },
                                jsPDF: { 
                                    unit: 'mm', 
                                    format: 'a4', 
                                    orientation: 'landscape' 
                                }
                            };
                            
                            // Show loading indicator
                            const originalText = document.querySelector('.btn-pdf').textContent;
                            document.querySelector('.btn-pdf').textContent = 'Generating PDF...';
                            
                            // Generate PDF
                            html2pdf().set(opt).from(element).save().then(() => {
                                // Restore button text
                                document.querySelector('.btn-pdf').textContent = originalText;
                            });
                        }
                        
                        window.onload = function() {
                            // Focus the window for better UX
                            window.focus();
                        };
                    <\/script>
                </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();

        printWindow.onafterprint = () => {
            itemsPerPage.value = originalItemsPerPage;
        };
    }, 100);
};

const clearFilters = () => {
    startDate.value = '';
    endDate.value = '';
    selectedViolationId.value = '';
    searchValue.value = '';
};
</script>

<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>Detailed Reports</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <h3 class="mb-0">Violation Action Reports.
                            <strong v-if="selectedViolationId">
                                Showing {{ currentViolationCount }} of {{ violationCounts.all.count }} total
                                reports
                                (Filtered by: {{violations.find(v => v.id == selectedViolationId)?.title}})
                            </strong>
                            <strong v-else>
                                Showing all {{ violationCounts.all.count }} reports
                            </strong>
                        </h3>

                        <!-- Filter controls -->
                        <div class="report-header mt-3">
                            <div class="filter-row row g-2 mb-2">
                                <div class="col-12 col-md-6 col-lg-3">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>From:</label>
                                        <input type="date" v-model="startDate" class="form-control">
                                    </div>
                                </div>
                                <div class="col-12 col-md-6 col-lg-3">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>To:</label>
                                        <input type="date" v-model="endDate" class="form-control">
                                    </div>
                                </div>
                                <div class="col-12 col-md-6 col-lg-3">
                                    <select v-model="selectedShift" class="form-select">
                                        <option value="">All Shifts</option>
                                        <option value="day">Day Shift (08:30-20:30)</option>
                                        <option value="night">Night Shift (20:30-08:30)</option>
                                    </select>
                                </div>
                                <div class="col-12 col-md-6 col-lg-3">
                                    <button class="btn btn-danger w-100" @click="clearFilters">
                                        Clear Filters
                                    </button>
                                </div>
                            </div>

                            <div class="filter-row row g-2 mb-3">
                                <div class="col-12 col-md-9">
                                    <input type="text" v-model="searchValue"
                                        placeholder="Search by driver, vehicle, route..." class="form-control">
                                </div>
                                <div class="col-12 col-md-3">
                                    <button v-if="hasPrintABSDetailedReportsPermission"
                                        class="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                                        @click="printReport">
                                        Print Report
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round"
                                            class="feather feather-printer">
                                            <polyline points="6 9 6 2 18 2 18 9"></polyline>
                                            <path
                                                d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2">
                                            </path>
                                            <rect x="6" y="14" width="12" height="8"></rect>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="violation-summary mb-3">
                            <div class="d-flex flex-wrap gap-2">
                                <button class="btn btn-sm"
                                    :class="!selectedViolationId ? 'btn-primary' : 'btn-outline-primary'"
                                    @click="selectedViolationId = ''">
                                    All ({{ filteredViolationCounts.all.count }})
                                </button>
                                <button v-for="violation in violations" :key="violation.id" class="btn btn-sm"
                                    :class="selectedViolationId == violation.id ? 'btn-info' : 'btn-outline-info'"
                                    @click="selectedViolationId = violation.id">
                                    {{ violation.title }} ({{ filteredViolationCounts[violation.id]?.count || 0 }})
                                </button>
                            </div>

                            <div class="d-flex flex-wrap gap-2 mt-2">
                                <button class="btn btn-sm"
                                    :class="!selectedShift ? 'btn-secondary' : 'btn-outline-secondary'"
                                    @click="selectedShift = ''">
                                    All Shifts ({{ shiftCounts.all }})
                                </button>
                                <button class="btn btn-sm"
                                    :class="selectedShift === 'day' ? 'btn-warning' : 'btn-outline-warning'"
                                    @click="selectedShift = 'day'">
                                    Day Shift ({{ shiftCounts.day }})
                                </button>
                                <button class="btn btn-sm"
                                    :class="selectedShift === 'night' ? 'btn-dark' : 'btn-outline-dark'"
                                    @click="selectedShift = 'night'">
                                    Night Shift ({{ shiftCounts.night }})
                                </button>
                            </div>
                        </div>

                        <!-- Items per page selector -->
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <div class="d-flex align-items-center">
                                <span class="me-2">Show:</span>
                                <select v-model="itemsPerPage" class="form-select form-select-sm" style="width: 80px;">
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <span class="ms-2">entries</span>
                            </div>
                            <div class="text-muted">
                                Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                                {{ Math.min(currentPage * itemsPerPage, filteredActions.length) }} of
                                {{ filteredActions.length }} entries
                            </div>
                        </div>

                        <!-- Loading state -->
                        <div v-if="isLoading" class="text-center p-4">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="mt-2">Loading reports...</p>
                        </div>

                        <!-- Data table -->
                        <div v-else class="table-responsive">
                            <table class="table table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>S/N</th>
                                        <!-- <th>Date</th> -->
                                        <th>Driver</th>
                                        <th>Conductor</th>
                                        <th>Vehicle</th>
                                        <th>Route</th>
                                        <th>Schedule</th>
                                        <!-- Dynamic parameter headers -->
                                        <th v-for="param in currentViolationParams" :key="param">{{ param }}</th>
                                        <th>Action Taken</th>
                                        <th v-if="hasEditPermission || hasDeletePermission">Actions</th>
                                        <!-- Fallback for when no violation is selected -->
                                        <!-- <th v-if="!selectedViolationId">Parameters</th> -->
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(action, index) in paginatedActions" :key="action.id || index">
                                        <td>{{ (currentPage - 1) * itemsPerPage + index + 1 }}</td>
                                        <!-- <td>{{ formatDate(action.created_at) }}</td> -->
                                        <td>{{ getDriverName(action.driver_id) }}</td>
                                        <td>{{ action.conductor_id || '—' }}</td>
                                        <td>{{ action.bus_registration || '—' }}</td>
                                        <td>{{ action.route || '—' }}</td>
                                        <td>{{ action.schedule || '—' }}</td>

                                        <!-- Dynamic parameter cells -->
                                        <template v-if="selectedViolationId">
                                            <td v-for="param in currentViolationParams" :key="param">
                                                {{ action.parameters?.[param] || '—' }}
                                            </td>
                                        </template>
                                        <td>{{ getActionTakenName(action.taken_action_id) }}</td>
                                        <td v-if="hasEditPermission || hasDeletePermission" class="text-nowrap">
                                            <button v-if="hasEditPermission" @click="openEditModal(action)"
                                                class="btn btn-sm btn-icon btn-primary me-1" title="Edit">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    fill="currentColor" viewBox="0 0 16 16">
                                                    <path
                                                        d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
                                                </svg>
                                            </button>
                                            <button v-if="hasDeletePermission" @click="confirmDelete(action)"
                                                class="btn btn-sm btn-icon btn-danger" title="Delete">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    fill="currentColor" viewBox="0 0 16 16">
                                                    <path
                                                        d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                                    <path fill-rule="evenodd"
                                                        d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                                                </svg>
                                            </button>
                                        </td>
                                        <!-- <template v-else>
                                            <td>
                                                <div v-for="(value, key) in action.parameters" :key="key">
                                                    <strong>{{ key }}:</strong> {{ value || '—' }}
                                                </div>
                                            </td>
                                        </template> -->
                                    </tr>
                                    <tr v-if="filteredActions.length === 0">
                                        <td :colspan="8 + (selectedViolationId ? currentViolationParams.length : 1)"
                                            class="text-center text-muted">
                                            No reports found matching your criteria
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Pagination -->
                            <div class="paginating-container pagination-solid flex-column align-items-center mt-4">
                                <ul role="menubar" aria-disabled="false" aria-label="Pagination"
                                    class="pagination mb-4 b-pagination">
                                    <li role="presentation" class="page-item first"
                                        :class="{ disabled: currentPage === 1 }">
                                        <button role="menuitem" type="button" @click="firstPage"
                                            :disabled="currentPage === 1" :tabindex="currentPage === 1 ? -1 : 0"
                                            aria-label="Go to first page" class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item prev"
                                        :class="{ disabled: currentPage === 1 }">
                                        <button role="menuitem" type="button" @click="prevPage"
                                            :disabled="currentPage === 1" :tabindex="currentPage === 1 ? -1 : 0"
                                            aria-label="Go to previous page" class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M15 19l-7-7 7-7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item" v-for="page in totalPages" :key="page"
                                        :class="{ active: currentPage === page }">
                                        <button role="menuitemradio" type="button" @click="goToPage(page)"
                                            :aria-label="`Go to page ${page}`" :aria-checked="currentPage === page"
                                            :aria-posinset="page" :aria-setsize="totalPages"
                                            :tabindex="currentPage === page ? 0 : -1" class="page-link">
                                            {{ page }}
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item next"
                                        :class="{ disabled: currentPage === totalPages }">
                                        <button role="menuitem" type="button" @click="nextPage"
                                            :disabled="currentPage === totalPages"
                                            :tabindex="currentPage === totalPages ? -1 : 0" aria-label="Go to next page"
                                            class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M9 5l7 7-7 7"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li role="presentation" class="page-item last"
                                        :class="{ disabled: currentPage === totalPages }">
                                        <button role="menuitem" type="button" @click="lastPage"
                                            :disabled="currentPage === totalPages"
                                            :tabindex="currentPage === totalPages ? -1 : 0" aria-label="Go to last page"
                                            class="page-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M13 5l7 7-7 7M5 5l7 7-7 7">
                                                </path>
                                            </svg>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal fade" :class="{ show: showEditModal, 'd-block': showEditModal }" tabindex="-1" role="dialog"
            v-if="showEditModal">
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Edit Violation Action</h5>
                        <button type="button" class="btn-close" @click="showEditModal = false"
                            aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <form @submit.prevent="submitEdit">
                            <div class="row">
                                <div class="col-md-6 mb-3">
                                    <label class="form-label">Driver</label>
                                    <input type="text" class="form-control" v-model="editForm.driver_id" required>
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label class="form-label">Conductor</label>
                                    <input type="text" class="form-control" v-model="editForm.conductor_id">
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label class="form-label">Vehicle Registration</label>
                                    <input type="text" class="form-control" v-model="editForm.bus_registration"
                                        required>
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label class="form-label">Route</label>
                                    <input type="text" class="form-control" v-model="editForm.route">
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label class="form-label">Schedule</label>
                                    <input type="text" class="form-control" v-model="editForm.schedule">
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label class="form-label">Action Taken</label>
                                    <select class="form-select" v-model="editForm.taken_action_id" required>
                                        <option v-for="action in actionsTaken" :key="action.id" :value="action.id">
                                            {{ action.name }}
                                        </option>
                                    </select>
                                </div>
                                <!-- Add other fields as needed -->
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="showEditModal = false">Cancel</button>
                        <button type="button" class="btn btn-primary" @click="submitEdit" :disabled="isSubmitting">
                            <span v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status"
                                aria-hidden="true"></span>
                            {{ isSubmitting ? 'Saving...' : 'Save Changes' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal-backdrop fade" :class="{ show: showEditModal }" v-if="showEditModal"></div>
    </div>
</template>

<style scoped>
.btn-outline-warning {
    color: #ffc107;
    border-color: #ffc107;
}

.btn-outline-dark {
    color: #212529;
    border-color: #212529;
}

.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.report-header {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    margin-bottom: 1.5rem;
}

.filter-row {
    align-items: center;
}

.filter-row label {
    white-space: nowrap;
    margin-bottom: 0;
    font-weight: 500;
}

.text-success {
    color: #28a745;
}

.text-warning {
    color: #ffc107;
}

.text-danger {
    color: #dc3545;
}

.table-responsive {
    overflow-x: auto;
}

@media print {

    .report-header,
    .breadcrumb-one,
    .pagination {
        display: none !important;
    }
}

@media (max-width: 767.98px) {
    .filter-row>div {
        margin-bottom: 0.5rem;
    }
}

.violation-summary {
    padding: 0.75rem;
    border-radius: 4px;
}

.violation-summary .btn {
    min-width: 120px;
    text-align: center;
}

/* Pagination styles */
.pagination {
    display: flex;
    padding-left: 0;
    list-style: none;
    border-radius: 0.25rem;
}

.page-item {
    margin: 0 2px;
}

.page-item.disabled .page-link {
    color: #6c757d;
    pointer-events: none;
    background-color: #fff;
    border-color: #dee2e6;
}

.page-item.active .page-link {
    z-index: 3;
    color: #fff;
    background-color: #3b3f5c;
    border-color: #3b3f5c;
}

.page-link {
    position: relative;
    display: block;
    padding: 0.5rem 0.75rem;
    margin-left: -1px;
    line-height: 1.25;
    color: #3b3f5c;
    background-color: #fff;
    border: 1px solid #dee2e6;
    text-decoration: none;
}

.page-link:hover {
    z-index: 2;
    color: #1d1d1d;
    background-color: #e9ecef;
    border-color: #dee2e6;
}

.page-link:focus {
    z-index: 3;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(59, 63, 92, 0.25);
}

.btn-excel {
    background-color: #1d6f42;
    color: white;
}

.column-selection {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 4px;
    margin-bottom: 20px;
}

.form-check {
    margin-right: 15px;
}

.form-check-input {
    margin-right: 5px;
}
</style>